import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import {
  clearStaffTokens,
  hasStoredStaffSession,
  onStaffAuthFailure,
  restoreStaffSession,
  setStaffTokens,
  staffLogin,
} from "@/lib/staff-api";
import type { StaffUser } from "@/lib/staff-api";
import { StaffAuthContext } from "@/lib/use-staff-auth";
import type { StaffAuthContextValue } from "@/lib/use-staff-auth";

/** Every staff query is keyed under "staff". Drop them when the session ends,
 *  or the next person to sign in on this tab sees the previous one's queues. */
function dropStaffQueries(queryClient: QueryClient): void {
  queryClient.removeQueries({ queryKey: ["staff"] });
}

export function StaffAuthProvider({ children }: { children: ReactNode }): React.ReactElement {
  const [staff, setStaff] = useState<StaffUser | null>(null);
  const [restoring, setRestoring] = useState<boolean>(() => hasStoredStaffSession());
  const queryClient = useQueryClient();

  const endSession = useCallback((): void => {
    clearStaffTokens();
    setStaff(null);
    dropStaffQueries(queryClient);
  }, [queryClient]);

  useEffect(() => {
    onStaffAuthFailure(endSession);
  }, [endSession]);

  useEffect(() => {
    if (!restoring) return;
    let active = true;
    void restoreStaffSession().then((restored) => {
      if (!active) return;
      if (restored) setStaff(restored);
      setRestoring(false);
    });
    return () => {
      active = false;
    };
    // Runs once on mount; restoreStaffSession is deduplicated for StrictMode.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<StaffUser> => {
    const result = await staffLogin(email, password);
    setStaffTokens(result.accessToken, result.refreshToken);
    setStaff(result.staff);
    return result.staff;
  }, []);

  const value = useMemo<StaffAuthContextValue>(
    () => ({ staff, isAuthenticated: staff !== null, login, logout: endSession, restoring }),
    [staff, login, endSession, restoring],
  );

  return <StaffAuthContext.Provider value={value}>{children}</StaffAuthContext.Provider>;
}
